import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { fetchProfile, login as loginApi, logout as logoutApi } from '@/api/user'
import { clearAuth, saveToken } from '@/utils/auth'
import type { LoginParams, ProfileVO, RoleCode } from '@/api/types'

/**
 * 用户/权限状态：token + 角色 + 权限码
 */
export const useUserStore = defineStore(
  'user',
  () => {
    const accessToken = ref('')
    const refreshToken = ref('')
    const profile = ref<ProfileVO | null>(null)
    const roles = ref<RoleCode[]>([])
    const permissions = ref<string[]>([])
    const remember = ref(false)
    /** 是否已拉取过用户信息 */
    const loaded = ref(false)

    const isLogin = computed(() => Boolean(accessToken.value))
    const isSuperAdmin = computed(() => roles.value.includes('SUPER_ADMIN'))
    /** 后端只区分 userType（1 普通 / 2 管理员），管理员即拥有管理端权限 */
    const isAdmin = computed(() => isSuperAdmin.value || roles.value.includes('ADMIN'))
    const displayName = computed(
      () => profile.value?.nickname || profile.value?.username || '未登录'
    )
    const avatar = computed(() => profile.value?.avatarUrl ?? '')

    /** 按钮级/路由级权限判断：超级管理员拥有全部权限 */
    function hasPermission(code?: string | string[]): boolean {
      if (!code) return true
      if (isSuperAdmin.value) return true
      // 后端未下发权限码（仅 userType）时，管理员默认放行，由后端 @PreAuthorize 兜底
      if (isAdmin.value && permissions.value.length === 0) return true
      const list = Array.isArray(code) ? code : [code]
      if (isAdmin.value) return list.every((item) => item.startsWith('admin:'))
      return list.some((item) => permissions.value.includes(item))
    }

    function hasRole(role?: RoleCode | RoleCode[]): boolean {
      if (!role) return true
      const list = Array.isArray(role) ? role : [role]
      if (isSuperAdmin.value) return true
      return list.some((item) => roles.value.includes(item))
    }

    async function login(params: LoginParams): Promise<void> {
      const data = await loginApi(params)
      accessToken.value = data.accessToken
      refreshToken.value = data.refreshToken
      remember.value = params.remember ?? false
      saveToken(
        { accessToken: data.accessToken, refreshToken: data.refreshToken },
        params.remember ?? false
      )
      await loadProfile(true)
    }

    async function loadProfile(force = false): Promise<void> {
      if (loaded.value && !force) return
      const data = await fetchProfile()
      profile.value = data
      roles.value = data.roles ?? []
      permissions.value = data.permissions ?? []
      loaded.value = true
    }

    function reset(): void {
      accessToken.value = ''
      refreshToken.value = ''
      profile.value = null
      roles.value = []
      permissions.value = []
      loaded.value = false
      clearAuth()
    }

    async function logout(): Promise<void> {
      try {
        await logoutApi()
      } catch {
        // 退出接口失败也要清理本地状态
      } finally {
        reset()
      }
    }

    return {
      accessToken,
      refreshToken,
      profile,
      roles,
      permissions,
      remember,
      loaded,
      isLogin,
      isSuperAdmin,
      isAdmin,
      displayName,
      avatar,
      hasPermission,
      hasRole,
      login,
      loadProfile,
      logout,
      reset,
    }
  },
  {
    persist: {
      key: 'wordapp-user',
      storage: localStorage,
      pick: ['accessToken', 'refreshToken', 'remember', 'roles', 'permissions', 'profile'],
    },
  }
)
